import { Link } from "react-router-dom";

export function HomePage() {
  return (
    <div>
      <h1 className="text-4xl font-semibold tracking-tight">
        Build AppSumo Outcomes integrations.
      </h1>
      <p className="mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">
        The AppSumo Outcomes marketplace lets Plus subscribers spend their
        monthly token stipend on finished work products — blog posts,
        images, SEO research, thumbnails, video cuts, more — delivered by
        SaaS partners integrated via this SDK.
      </p>

      <section className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <CardLink
          to="/quickstart/typescript"
          title="Quickstart — TypeScript"
          body={
            <>
              Zod schemas → <code>@appsumo/outcomes</code>. Ships on Node,
              Workers, Deno, Bun. Adapters for fetch + Hono.
            </>
          }
        />
        <CardLink
          to="/quickstart/python"
          title="Quickstart — Python"
          body={
            <>
              Pydantic schemas → <code>appsumo-outcomes</code>. Adapters for
              FastAPI + Starlette. Modal / Fly / anywhere with HTTPS.
            </>
          }
        />
        <CardLink
          to="/playground"
          title="Interactive API playground"
          body="Browse every Hub endpoint, generate code samples, try calls against the sandbox right from the docs."
        />
        <ExternalCard
          href="https://github.com/appsumo/sumo-outcomes"
          title="Source on GitHub"
          body="Monorepo: Hub API, three-language SDKs, CLI, marketplace UI, developer portal."
        />
      </section>

      <section className="mt-16">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
          Concepts
        </h2>
        <ul className="mt-4 space-y-3 text-sm">
          <Concept
            title="Signed webhooks (Stripe-compatible)"
            body={
              <>
                Hub → partner calls are signed with
                <code className="mx-1 rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs dark:bg-neutral-900">
                  Hub-Signature: t=&lt;unix_ms&gt;,v1=&lt;hex&gt;
                </code>
                — HMAC-SHA256 over <code>{`{t}.{raw_body}`}</code>. 300s
                tolerance, multi-<code>v1</code> for 24h rotation overlap.
                Verify via <code>verifyHubSignature()</code>.
              </>
            }
          />
          <Concept
            title="OAuth 2.0 client_credentials → RS256 JWT"
            body={
              <>
                Partner exchanges <code>client_id</code> +{" "}
                <code>client_secret</code> at
                <code className="mx-1 rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs dark:bg-neutral-900">
                  POST /oauth/token
                </code>{" "}
                for a 1h token. Scopes are verb:resource — see{" "}
                <Link
                  to="/playground"
                  className="text-emerald-600 underline dark:text-emerald-400"
                >
                  playground
                </Link>{" "}
                for the full list.
              </>
            }
          />
          <Concept
            title="Double-entry ledger with cohort tracking"
            body="Subscription tokens reset at renewal; topup tokens roll over 3 months. Holds survive cohort expiry via a 7-day rescue cohort on release. Per-row HMAC integrity."
          />
          <Concept
            title="Two integration models"
            body="Model A (Hub → Partner) is the primary flow driven by the marketplace. Model B (Partner → Hub) lets a partner's own app charge the user's wallet via OAuth authorization_code with PKCE — Stripe-Connect style."
          />
        </ul>
      </section>

      <section className="mt-16 rounded-xl border border-neutral-200 bg-neutral-50 p-6 dark:border-neutral-800 dark:bg-neutral-900">
        <h2 className="text-base font-semibold">Scaffold a partner in one command</h2>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-neutral-900 p-4 font-mono text-sm text-emerald-300">
          <code>npx create-hub-partner my-outcome --lang ts</code>
        </pre>
        <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
          Emits a working sample outcome, <code>AGENTS.md</code> for AI
          coding agents, <code>.env.example</code>, Dockerfile, and a test
          harness. Also <code>--lang python</code> and{" "}
          <code>--lang go</code>.
        </p>
      </section>

      <section className="mt-16 text-sm text-neutral-500">
        <p>
          Need to verify clock drift before signing? Call{" "}
          <a
            href="https://hub.sumo.com/v1/time"
            className="text-emerald-600 dark:text-emerald-400"
          >
            hub.sumo.com/v1/time
          </a>{" "}
          at SDK startup — the three SDKs warn if drift &gt; 30s and fail
          if &gt; 300s (webhooks would be rejected anyway).
        </p>
      </section>
    </div>
  );
}

function CardLink({
  to,
  title,
  body,
}: {
  to: string;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="group rounded-xl border border-neutral-200 bg-white p-5 transition hover:border-emerald-300 hover:shadow-sm dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-emerald-700"
    >
      <p className="text-base font-semibold group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
        {title} →
      </p>
      <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{body}</p>
    </Link>
  );
}

function ExternalCard({
  href,
  title,
  body,
}: {
  href: string;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group rounded-xl border border-neutral-200 bg-white p-5 transition hover:border-emerald-300 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-emerald-700"
    >
      <p className="text-base font-semibold group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
        {title} ↗
      </p>
      <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{body}</p>
    </a>
  );
}

function Concept({ title, body }: { title: string; body: React.ReactNode }) {
  return (
    <li className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
      <p className="font-medium">{title}</p>
      <p className="mt-1.5 text-neutral-600 dark:text-neutral-400">{body}</p>
    </li>
  );
}
